import { useTranslation } from 'react-i18next';
import {
  relationSelectableForReferral,
  type RelativeChoice,
} from '@/lib/referral/referralRelative';
import { WARNING_UNDER15_NO_RELATIVE } from '@/lib/referral/xref01Codes';
import { AlertCircle } from 'lucide-react';
import { Radio } from '@/components/ui/Radio';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

type RelationItem = {
  relationType: string;
  row: { id: string; target_name?: string | null };
};

/**
 * Pårørende on a referral: the clinician picks the one relative that is
 * sent (or none, from 15 years). Each row says whether it is included.
 * The under-15 warning shows only when no selectable relative can be sent.
 */
export function ReferralRelativesChoice({
  idPrefix,
  relations,
  selectedId,
  under15,
  childWithoutRelative,
  emptyLabel,
  onChoose,
}: {
  idPrefix: string;
  relations: readonly RelationItem[];
  selectedId: string | null;
  under15: boolean;
  childWithoutRelative: boolean;
  emptyLabel: string;
  onChoose: (choice: RelativeChoice) => void;
}) {
  const { t } = useTranslation();
  const name = `${idPrefix}-relative`;
  const anySelectable = relations.some((item) =>
    relationSelectableForReferral(item.relationType, under15),
  );
  return (
    <div>
      {relations.length === 0 ? (
        <p className={cn('mt-3', TYPE.body)}>
          {t('patients.hospital_referral.relatives_none', 'Ingen personrelationer.')}
        </p>
      ) : (
        <fieldset className="mt-3">
          <legend className="sr-only">
            {t('patients.hospital_referral.relatives_section', 'Pårørende')}
          </legend>
          <ul className="space-y-2">
            {relations.map((item) => {
              const selectable = relationSelectableForReferral(item.relationType, under15);
              const included = selectedId === item.row.id;
              const inputId = `${name}-${item.row.id}`;
              return (
                <li key={item.row.id} className={TYPE.body}>
                  <Radio
                    id={inputId}
                    name={name}
                    checked={included}
                    disabled={!selectable}
                    onChange={() => onChoose(item.row.id)}
                    label={
                      <span className={cn(!selectable && 'text-ds-muted')}>
                    {t(`patients.relations.types.${item.relationType}`, item.relationType)}
                    {': '}
                    {item.row.target_name || emptyLabel}
                    {' — '}
                    <span className={included ? 'font-medium' : undefined}>
                      {included
                        ? t('patients.hospital_referral.relatives_included', 'Medtages i henvisningen')
                        : selectable
                          ? t('patients.hospital_referral.relatives_not_included', 'Medtages ikke')
                          : t(
                              'patients.hospital_referral.relatives_not_parent',
                              'Kan ikke medtages i henvisningen',
                            )}
                    </span>
                      </span>
                    }
                  />
                </li>
              );
            })}
            {!under15 && anySelectable && (
              <li className={TYPE.body}>
                <Radio
                  id={`${name}-none`}
                  name={name}
                  checked={selectedId === null}
                  onChange={() => onChoose('none')}
                  label={t('patients.hospital_referral.relatives_choose_none', 'Ingen pårørende medtages')}
                />
              </li>
            )}
          </ul>
        </fieldset>
      )}
      {childWithoutRelative && (
        <div role="alert" className="hv-ferr">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden />
          <div>
            <p>
              {t('patients.hospital_referral.relatives_under15', WARNING_UNDER15_NO_RELATIVE)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
